/**
 * Parsed-file walk over every label *value* in the workspace.
 *
 * Unlike `forEachAspectValue` (core), which walks the resolved model and
 * loses per-file source identity once labels cascade, this walks the raw
 * parsed declarations so each occurrence keeps its real `uri` + span.
 * That is what reference-index / rename need: they rewrite source, not
 * resolved copies.
 *
 * A label value carries one of two cross-cutting meanings (see
 * `@archlang/engine` aspects):
 *   - string      → an aspect (group identity keyed by `(keyPath, value)`)
 *   - identifier  → a reference to a module named by the value
 * Lists are flattened: each item is classified on its own.
 */

import type {
  Body,
  SurfaceDecl,
  InterfaceBody,
  InterfaceDecl,
  AspectEntry,
  ModuleDecl,
  QualifiedName,
  SourceSpan,
  TypeDecl,
  Value,
} from "@archlang/engine";
import { classifyAspectValue, type AspectValueClass } from "@archlang/engine";
import type { Workspace } from "./workspace.js";

export interface ParsedAspectValue {
  readonly uri: string;
  /** Label key, e.g. `["security", "zone"]`. */
  readonly keyPath: QualifiedName;
  /** Atomic value (never a list — lists are flattened). */
  readonly value: Value;
  readonly cls: AspectValueClass;
  /** Qualified name of the owning module / surface / interface / type. */
  readonly ownerPath: QualifiedName;
  /** Span of the value token, falling back to the label entry span. */
  readonly valueSpan?: SourceSpan;
}

/** Walk every atomic label-value occurrence across all workspace files. */
export function forEachParsedAspectValue(
  workspace: Workspace,
  cb: (occ: ParsedAspectValue) => void
): void {
  for (const uri of workspace.uris()) {
    const file = workspace.getFile(uri);
    if (!file) continue;
    for (const top of file.file.declarations) {
      if (top.kind === "module") {
        visitModule(uri, top.decl, [], cb);
      } else if (top.kind === "type") {
        visitType(uri, top.decl, cb);
      }
    }
  }
}

function visitType(
  uri: string,
  decl: TypeDecl,
  cb: (occ: ParsedAspectValue) => void
): void {
  visitBody(uri, decl.body, [decl.name], cb);
}

function visitModule(
  uri: string,
  decl: ModuleDecl,
  parentPath: QualifiedName,
  cb: (occ: ParsedAspectValue) => void
): void {
  visitBody(uri, decl.body, [...parentPath, decl.name], cb);
}

function visitBody(
  uri: string,
  body: Body,
  ownerPath: QualifiedName,
  cb: (occ: ParsedAspectValue) => void
): void {
  emitAspects(uri, body.aspects, ownerPath, cb);
  for (const iface of body.interfaces) visitInterface(uri, iface, ownerPath, cb);
  for (const surface of body.surfaces) visitSurface(uri, surface, ownerPath, cb);
  for (const child of body.modules) visitModule(uri, child, ownerPath, cb);
}

function visitSurface(
  uri: string,
  decl: SurfaceDecl,
  parentPath: QualifiedName,
  cb: (occ: ParsedAspectValue) => void
): void {
  visitBody(uri, decl.body, [...parentPath, decl.name], cb);
}

function visitInterface(
  uri: string,
  decl: InterfaceDecl,
  parentPath: QualifiedName,
  cb: (occ: ParsedAspectValue) => void
): void {
  const body: InterfaceBody = decl.body;
  emitAspects(uri, body.aspects, [...parentPath, decl.name], cb);
}

function emitAspects(
  uri: string,
  labels: readonly AspectEntry[],
  ownerPath: QualifiedName,
  cb: (occ: ParsedAspectValue) => void
): void {
  for (const label of labels) {
    if (!label.value) continue;
    emitValue(uri, label.value, label.path, ownerPath, label.source, cb);
  }
}

function emitValue(
  uri: string,
  value: Value,
  keyPath: QualifiedName,
  ownerPath: QualifiedName,
  aspectSpan: SourceSpan | undefined,
  cb: (occ: ParsedAspectValue) => void
): void {
  if (value.kind === "list") {
    for (const item of value.items) {
      emitValue(uri, item, keyPath, ownerPath, aspectSpan, cb);
    }
    return;
  }
  cb({
    uri,
    keyPath,
    value,
    cls: classifyAspectValue(value),
    ownerPath,
    valueSpan: value.source ?? aspectSpan,
  });
}
